import type { TransactionApi, TransactionLineItemApi } from '@/types/api/transactions';
import type { Transaction, TransactionLineItem, TransactionStatus, TransactionType } from '@/types/transaction';

function mapTransactionTypeApiToUi(apiType: string): TransactionType {
  const typeMap: Record<string, TransactionType> = {
    invoice: 'invoice',
    credit_note: 'credit_note',
    receipt: 'receipt',
    debit_adjustment: 'debit_adjustment',
    credit_adjustment: 'credit_adjustment',
  };
  return typeMap[apiType.toLowerCase()] ?? 'invoice';
}

function mapTransactionStatusApiToUi(apiStatus: string): TransactionStatus {
  const statusMap: Record<string, TransactionStatus> = {
    outstanding: 'outstanding',
    completed: 'completed',
    overdue: 'overdue',
  };
  return statusMap[apiStatus.toLowerCase()] ?? 'outstanding';
}

function mapLineItemApiToUi(apiLineItem: TransactionLineItemApi): TransactionLineItem {
  return {
    id: apiLineItem.id,
    itemCode: apiLineItem.itemCode,
    description: apiLineItem.description,
    project: apiLineItem.project,
    projectName: apiLineItem.projectName,
    costCentre: apiLineItem.costCentre,
    costCentreName: apiLineItem.costCentreName,
    quantity: apiLineItem.quantity,
    unitPrice: apiLineItem.unitPrice,
    currencyUnitPrice: apiLineItem.currencyUnitPrice,
    subtotal: apiLineItem.subtotal,
    taxAmount: apiLineItem.taxAmount,
    total: apiLineItem.total,
    currencyGross: apiLineItem.currencyTotal,
    currencyNet: apiLineItem.currencySubtotal,
    currencyVAT: apiLineItem.currencyTaxAmount,
  };
}

export function mapTransactionApiToUi(apiTransaction: TransactionApi): Transaction {
  return {
    id: apiTransaction.id,
    referenceNumber: apiTransaction.referenceNumber || String(apiTransaction.id),
    transactionDate: apiTransaction.transactionDate,
    customerId: apiTransaction.customerId,
    customerName: apiTransaction.customerName,
    transactionType: mapTransactionTypeApiToUi(apiTransaction.transactionType),
    amount: apiTransaction.amount,
    currency: apiTransaction.currency,
    status: mapTransactionStatusApiToUi(apiTransaction.status),
    description: apiTransaction.description ?? '',
    notes: apiTransaction.notes,
    createdBy: apiTransaction.createdBy ?? '',
    modifiedBy: apiTransaction.modifiedBy,
    createdAt: apiTransaction.createdAt,
    modifiedAt: apiTransaction.modifiedAt,
    dueDate: apiTransaction.dueDate,
    headerReference: apiTransaction.headerReference,
    paymentMethod: apiTransaction.paymentMethod,
    documentUrl: apiTransaction.documentUrl,
    outstandingAmount: apiTransaction.outstandingAmount,
    onDispute: apiTransaction.onDispute,
    hasDispute: apiTransaction.hasDispute,
    accountCode: apiTransaction.accountCode,
    netAmount: apiTransaction.netAmount,
    vatAmount: apiTransaction.vatAmount,
    subLedger: apiTransaction.subLedger,
    subLedgerName: apiTransaction.subLedgerName,
    orderNumber: apiTransaction.orderNumber,
    transactionReference: apiTransaction.transactionReference,
    bankReference: apiTransaction.bankReference,
    currencyGross: apiTransaction.currencyAmount,
    currencyNet: apiTransaction.currencyNetAmount,
    currencyVAT: apiTransaction.currencyVatAmount,
    currencyOutstanding: apiTransaction.currencyOutstandingAmount,
    accountNumber: apiTransaction.accountNumber,
    daysOverdue: apiTransaction.daysOverdue,
    ageingBucket: apiTransaction.ageingBucket,
    hasPromise: apiTransaction.hasPromise,
    hasCase: apiTransaction.hasCase,
    allocationStatusCode: apiTransaction.allocationStatusCode,
    allocationStatusName: apiTransaction.allocationStatusName,
    totalAllocated: apiTransaction.totalAllocated,
    allocationCount: apiTransaction.allocationCount,
    externalId: apiTransaction.externalId,
    lineItems: apiTransaction.lineItems?.map(mapLineItemApiToUi),
  };
}
